/** Functions related to finding and calculating area in shapes. */
import proj4 from 'proj4';
import { centroid as getCentroid } from '3rd-party-wrappers/turf';
import { getGeometry } from 'geometry/geojson/convert';

const _areVertexesEqual = (v1, v2) => v1[0] === v2[0] && v1[1] === v2[1];

const _getSlope = (vertex, prevVertex) => {
  const dx = vertex[0] - prevVertex[0];
  const dy = vertex[1] - prevVertex[1];
  return dx === 0 ? Infinity : dy / dx;
};

const _areSlopesEqualish = (s1, s2) => {
  const ROUND_MULTIPLIER = 100000;
  return (s1 === Infinity)
    ? s2 === Infinity
    : Math.round(s1 * ROUND_MULTIPLIER) === Math.round(s2 * ROUND_MULTIPLIER);
};

const _doPolygonCoordinatesHaveArea = (polygonCoords) => {
  let prevSlope, prevVertex;
  // For each vertex on the outer ring...
  return polygonCoords[0].some(vertex => { // Return true if proved polygon has area, false otherwise.
    if (!prevVertex) {
      prevVertex = vertex;
      return false;
    }

    // Skip over duplicate vertexes, or slope will be miscalculated.
    if (_areVertexesEqual(prevVertex, vertex)) { return false; }

    // As soon as one vertex leaves a straight line, the polygon has area.
    const slope = _getSlope(vertex, prevVertex);
    if (prevSlope !== undefined && !_areSlopesEqualish(slope, prevSlope)) { return true; }

    prevSlope = slope;
    prevVertex = vertex;
    return false;
  });
};

/** Do a quick check to see if polygon has area without calculating its entire area. Behavior is
    undefined for the following cases:
    - Polygon with kinks. (invalid)
    - Polygon with interior rings that entirely cover area of exterior ring. (technically valid, but extremely weird)
    - Polygon with OOB interior rings. (invalid)
    @param polygon Valid Polygon that doesn't have any of the above problems.
    @return True, if polygon has area, or false, if not. */
export const doesPolygonHaveArea = (polygon) => _doPolygonCoordinatesHaveArea(polygon.coordinates);

/** Same as doesPolygonHaveArea() but also accepts MultiPolygons. */
export const doesXPolygonHaveArea = (xPolygon) => {
  const multiPolygonCoords = xPolygon.type === 'Polygon'
    ? [xPolygon.coordinates] : xPolygon.coordinates;
  return multiPolygonCoords.some(polygonCoords => _doPolygonCoordinatesHaveArea(polygonCoords));
};

/**
 * Calculate area based on coordinates
 * @param {array} coords - coordinates for geometry
 * @return {number} area
 */

const calcArea = (coords) => {
  let area = 0.0;
  let j = coords.length - 1;

  for (let i = 0; i < coords.length; i++) {
    const x = (coords[j][0] + coords[i][0]);
    const y = (coords[j][1] - coords[i][1]);
    area += x * y;
    j = i;
  }

  return area * 0.5;
};

/**
 * Generate WKT for LAEA projection
 * This function is from
 * https://git.tcc.li/projects/CTP/repos/tcc-libspatial/browse/src/main/clojure/com/climate/spatial/geometry.clj#47
 * @param {array} centroid - centroid for projection
 * @return {string} LAEA WKT
 */

const getLAEAWKT = (centroid) => `
  PROJCS["WGS84_To_LAEA",
    GEOGCS["GCS_WGS_1984",
      DATUM["D_WGS_1984",
        SPHEROID["WGS_1984",6378137.0,298.257223563]],
      PRIMEM["Greenwich",0.0],
      UNIT["Degree",0.0174532925199433]],
    PROJECTION["Lambert_Azimuthal_Equal_Area"],
    PARAMETER["False_Easting",0.0],
    PARAMETER["False_Northing",0.0],
    PARAMETER["Central_Meridian",${centroid[0]}],PARAMETER["Latitude_Of_Origin",${centroid[1]}],
    UNIT["Meter",1.0]]
`;

/**
 * Calculate area for the polygon in LAEA projection
 * @param {array} coordinates - coordinates for polygon
 * @param {array} centroid - centroid for LAEA projection
 * @return {number} Area of Polygon in hectare
 */

const calcPolygonArea = (coordinates, centroid) => {
  return coordinates
  // project WSG84 to LAEA
  .map(coords => coords.map(coord => proj4('EPSG:4326', getLAEAWKT(centroid), coord)))
  .reduce((previous, current, index) => {
    const area = Math.abs(calcArea(current));
    if (index === 0) {
      return area;
    }
    return previous - area;
  }, 0)
  * 0.0001; // convert m^2 to hectare
};

/**
 * Calculate area based on provided zoneGeoJson
 * @param {object} zoneGeoJson - geoJson for the specific zone
 * @return {number} area of the zone in hectare
 */

export const area = (zoneGeoJson) => {
  const geometry = getGeometry(zoneGeoJson);
  const centroid = getCentroid(geometry).geometry.coordinates;
  let area = 0;
  if (geometry.type === 'Polygon') {
    area = calcPolygonArea(geometry.coordinates, centroid);
  } else if (geometry.type === 'MultiPolygon') {
    area = geometry.coordinates.reduce((previous, current) => {
      return previous + calcPolygonArea(current, centroid);
    }, 0);
  }
  
  if (area) {
    return area
     - (-0.00000000706 - 0.0000227 * area -2.39E-12 * Math.pow(area, 2)
       + 3.18E-14 * Math.pow(area, 3) - 1.56E-17 * Math.pow(area, 4));
  }

  /*
   * 4th order polynomial regression of client/server differences in calculations
   * This equation corrects the difference between the two platforms
   */

  throw new Error(`${zoneGeoJson.type} is not supported for area calculation`);
};